import { useState } from 'react';
import { Link } from 'react-router';
import { useCan } from '../lib/auth';
import { useStartOrder } from './Orders';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import { fmtDate, fmtMoney, fmtNumber } from '../lib/format';
import type { PriceItem, PriceList as PriceListT } from '../lib/types';
import { Button, Callout, Checkbox, Empty, ErrorState, Input, Ltr, SkeletonRows, useDebounced, useToast } from './ui';
import { IconDownload } from './icons';
import { useOpenDocument } from './Layout';
import { MarginBadge } from './Margin';
import { marginOf } from '../lib/profit';

const SOURCE = { manual: 'מחיר ידני', last: 'מחיר אחרון', base: 'מחיר בסיס', none: 'אין מחיר' } as const;

/** Customer price list: every catalog product at the price this customer pays (manual → last quoted/sold → base). */
export function PriceList({ customerId, parentName }: { customerId: number; parentName?: string | null }) {
  const [q, setQ] = useState('');
  // The whole catalog by default (the customer's own prices first) — a new customer gets every product at the base price.
  const [own, setOwn] = useState(false);
  const [low, setLow] = useState(false);
  const dq = useDebounced(q);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const can = useCan();
  const { start, busy: starting } = useStartOrder();
  const { data, error, refetch } = useQuery({
    queryKey: ['price-list', customerId, dq, own, low],
    queryFn: () => api<PriceListT>(`/api/customers/${customerId}/prices${qs({ q: dq, own: own ? '1' : undefined, low: low ? '1' : undefined })}`),
    placeholderData: (prev) => prev,
  });

  const costs = Boolean(data?.profit);
  const cols = 6 + (costs ? 2 : 0);
  if (error) return <div className="panel"><ErrorState error={error} onRetry={() => refetch()} /></div>;

  if (data && data.counts.products === 0) {
    return (
      <div className="panel">
        <Empty
          title="אין עדיין קטלוג מוצרים"
          body="אחרי ייבוא הקטלוג, המחירון של הלקוח נבנה אוטומטית מהמחירים שהוצעו ונמכרו לו."
          actions={<Link to="/products/import" className="btn primary">ייבוא קטלוג</Link>}
        />
      </div>
    );
  }

  return (
    <div className="stack">
      {parentName ? <Callout>המחירון משותף לכל הסניפים של {parentName}.</Callout> : null}
      {data && data.unmatchedLines > 0 ? (
        <Callout tone="info" action={<Link to="/products/matching" className="btn sm">לשיוך</Link>}>
          {fmtNumber(data.unmatchedLines)} שורות במסמכים של הלקוח עוד לא משויכות למוצר מהקטלוג, ולכן המחיר שלהן לא נכנס למחירון.
        </Callout>
      ) : null}
      {data?.profit ? (
        <section className="kpis" aria-label="רווחיות המחירון">
          <div className="kpi" title={data.profit.weightedBy === 'bought' ? 'ממוצע משוקלל לפי הכמויות שהלקוח קנה ב-12 החודשים האחרונים, במחירים שלו היום' : 'ממוצע פשוט של המוצרים עם מחיר ללקוח (לא נמצאו קניות ב-12 החודשים האחרונים)'}>
            <div className="kpi-label">רווחיות המחירון</div>
            <div className="kpi-value"><MarginBadge margin={data.profit.margin} level={data.profit.level} /></div>
            <div className="kpi-meta">{data.profit.weightedBy === 'bought' ? 'משוקלל לפי מה שקנה בשנה האחרונה' : 'ממוצע פשוט'} · יעד {data.profit.target}%</div>
          </div>
          <button className={`kpi kpi-button ${low ? 'active' : ''}`} aria-pressed={low} onClick={() => setLow(!low)}>
            <div className="kpi-label">מוצרים מתחת ליעד</div>
            <div className="kpi-value" style={data.profit.belowTarget ? { color: 'var(--warn)' } : undefined}>{fmtNumber(data.profit.belowTarget)}</div>
            <div className="kpi-meta">מתוך {fmtNumber(data.profit.withCost)} עם מחיר ללקוח ועלות · {low ? 'לחיצה מבטלת סינון' : 'לחיצה מסננת'}</div>
          </button>
          <div className="kpi">
            <div className="kpi-label">מתחת לעלות</div>
            <div className="kpi-value" style={data.profit.belowCost ? { color: 'var(--bad)' } : undefined}>{fmtNumber(data.profit.belowCost)}</div>
            <div className="kpi-meta">מוצרים שהלקוח משלם עליהם פחות מהעלות</div>
          </div>
        </section>
      ) : null}
      <section className="panel">
        <div className="panel-head row wrap" style={{ gap: 10 }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="חיפוש מוצר או מק״ט" style={{ maxWidth: 280 }} aria-label="חיפוש מוצר" />
          <Checkbox checked={own} onChange={setOwn}>רק מוצרים עם מחיר ללקוח {data ? `(${fmtNumber(data.counts.own)})` : ''}</Checkbox>
          <span className="spacer" />
          {data ? <span className="small muted">{fmtNumber(data.counts.fromDocuments)} ממסמכים · {fmtNumber(data.counts.manual)} ידניים · המחירים לפני מע״מ</span> : null}
          <a className="btn sm" href={`/api/customers/${customerId}/prices/export${qs({ own: own ? '1' : undefined })}`}>
            <IconDownload /> Excel
          </a>
        </div>
        {picked.size && can('orders.edit') ? (
          <div className="panel-body row wrap" style={{ gap: 8, background: 'var(--accent-soft)' }}>
            <span className="strong">{fmtNumber(picked.size)} מוצרים מסומנים</span>
            <Button size="sm" variant="primary" loading={starting} onClick={() => start(customerId, undefined, [...picked])}>
              הצעת מחיר מהמסומנים
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setPicked(new Set())}>ניקוי הסימון</Button>
            <span className="small muted">נפתחת טיוטה במחירים של הלקוח — משנים כמויות ומפיקים.</span>
          </div>
        ) : null}
        <div className="table-wrap" style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                {can('orders.edit') ? (
                  <th style={{ width: 36 }}>
                    <Checkbox
                      checked={Boolean(data?.items.length) && data!.items.every((i) => picked.has(i.productId))}
                      onChange={(v) => setPicked(v ? new Set(data?.items.map((i) => i.productId)) : new Set())}
                    >
                      <span className="sr-only">סימון הכל</span>
                    </Checkbox>
                  </th>
                ) : null}
                <th>מק״ט</th>
                <th>מוצר</th>
                <th className="num">מחיר ללקוח</th>
                <th className="num hide-sm">מחיר בסיס</th>
                {costs ? <th className="num hide-sm">עלות</th> : null}
                {costs ? <th className="num" title="(מחיר ללקוח − עלות) ÷ מחיר ללקוח">רווחיות</th> : null}
                <th>מקור</th>
                <th style={{ width: 1 }} />
              </tr>
            </thead>
            <tbody>
              {!data ? (
                <SkeletonRows rows={8} cols={cols} />
              ) : data.items.length === 0 ? (
                <tr>
                  <td colSpan={cols}>
                    <Empty title={low ? 'אין מוצרים מתחת ליעד' : own ? 'אין עדיין מחירים ייחודיים ללקוח' : 'לא נמצאו מוצרים'} body={own ? 'בטלו את הסינון כדי לראות את כל הקטלוג במחירי בסיס, ולקבוע מחיר ידני.' : undefined} />
                  </td>
                </tr>
              ) : (
                data.items.map((i) => (
                  <PriceRow
                    key={i.productId}
                    item={i}
                    customerId={customerId}
                    costs={costs}
                    target={data.profit?.target ?? 30}
                    warn={data.profit?.warn ?? 20}
                    picked={can('orders.edit') ? picked.has(i.productId) : undefined}
                    onPick={(v) => {
                      const n = new Set(picked);
                      if (v) n.add(i.productId);
                      else n.delete(i.productId);
                      setPicked(n);
                    }}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function PriceRow({ item, customerId, costs, target, warn, picked, onPick }: { item: PriceItem; customerId: number; costs: boolean; target: number; warn: number; picked?: boolean; onPick: (v: boolean) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();
  const openDoc = useOpenDocument();

  const save = async (price: number | null) => {
    setBusy(true);
    try {
      await api(`/api/customers/${customerId}/prices/${item.productId}`, { method: 'PUT', body: { price } });
      toast.success(price == null ? 'המחיר הידני הוסר' : 'המחיר נשמר');
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['price-list'] });
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const submit = () => {
    const n = Number(value.replace(/[₪,\s]/g, ''));
    if (!value.trim() || !Number.isFinite(n) || n < 0) return toast.error(new Error('מחיר לא תקין'));
    void save(Math.round(n * 100) / 100);
  };

  return (
    <tr>
      {picked !== undefined ? (
        <td>
          <Checkbox checked={picked} onChange={onPick}>
            <span className="sr-only">סימון</span>
          </Checkbox>
        </td>
      ) : null}
      <td className="tabular small"><Ltr>{item.sku}</Ltr></td>
      <td className="primary">
        {item.name}
        {item.category ? <span className="sub">{item.category}</span> : null}
      </td>
      <td className="num tabular">
        {editing ? (
          <form
            className="row"
            style={{ gap: 4, justifyContent: 'flex-end' }}
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <Input className="price-input" value={value} onChange={(e) => setValue(e.target.value)} autoFocus inputMode="decimal" aria-label="מחיר ללקוח" onKeyDown={(e) => e.key === 'Escape' && setEditing(false)} />
            <Button size="sm" variant="primary" type="submit" loading={busy}>שמירה</Button>
            {costs && item.cost != null ? (() => {
              const m = marginOf(Number(value.replace(/[₪,\s]/g, '')), item.cost, target, warn);
              return <MarginBadge margin={m.margin} level={m.level} />;
            })() : null}
          </form>
        ) : (
          <>
            <span className={item.source === 'manual' || item.source === 'last' ? 'strong' : item.source === 'none' ? 'faint' : undefined}>{fmtMoney(item.price)}</span>
            {item.diffPct ? (
              <span className="sub" style={{ color: item.diffPct < 0 ? 'var(--warn)' : 'var(--muted)' }}>
                <bdi>{item.diffPct > 0 ? '+' : ''}{item.diffPct}%</bdi> מהבסיס
              </span>
            ) : null}
          </>
        )}
      </td>
      <td className="num tabular hide-sm muted">{fmtMoney(item.basePrice)}</td>
      {costs ? (
        <td className="num tabular hide-sm muted" title={item.costFromPack ? 'עלות החבילה × חבילות בארגז' : undefined}>
          {item.cost == null ? <span className="faint">—</span> : fmtMoney(item.cost)}
        </td>
      ) : null}
      {costs ? (
        <td className="num">
          {item.priceCheck ? <span className="badge warn" title="המחיר לא מתאים לעלות — כנראה מחיר ליחידה בודדת. כדאי לקבוע מחיר לחבילה">לבדיקה</span> : <MarginBadge margin={item.margin} level={item.marginLevel} />}
          {item.bought ? <span className="sub" title="חבילות שהלקוח קנה ב-12 החודשים האחרונים">{fmtNumber(item.bought)} נקנו בשנה</span> : null}
        </td>
      ) : null}
      <td className="small">
        <span className={`badge src-badge ${item.source}`}>{SOURCE[item.source]}</span>
        {item.source === 'base' && item.baseEstimate ? (
          <span className="sub" title="למוצר אין מחיר בסיס בקטלוג — המחיר נקבע אוטומטית. מחיר בסיס שתקליד במוצר יגבר">
            מחיר בסיס אוטומטי — {item.baseEstimate === 'carton' ? 'מחיר החבילה × חבילות בארגז' : item.baseEstimate === 'market' ? 'לפי המחיר האחרון ששולם' : 'לפי העלות ביעד הרווחיות'}
          </span>
        ) : null}
        {item.source === 'last' && item.last ? (
          <span className="sub">
            {item.last.archive ? (
              <span title="מחשבון MARKIT — היסטוריית מחירים">{item.last.typeLabel} {item.last.number ?? ''}</span>
            ) : (
              <button type="button" className="link-button" onClick={() => openDoc(item.last!.documentId)}>
                {item.last.typeLabel} {item.last.number ?? ''}
              </button>
            )}
            {' '}· {fmtDate(item.last.date)}
            {item.last.branch ? ` · ${item.last.branch}` : ''}
            {item.last.viaVariant ? ` · נקנה ב${item.last.viaVariant}` : ''}
            {item.last.perUnit ? ` · חושב מ-${fmtMoney(item.last.perUnit.unitPrice)} ליח׳ × ${item.last.perUnit.units}` : ''}
            {item.last.fromPack ? ` · מחושב מהמחיר לחבילה ${fmtMoney(item.last.fromPack.packPrice)} × ${item.last.fromPack.packs}` : ''}
            {item.manual ? ` · המחיר הידני (${fmtMoney(item.manual.price)}) הוחלף במחיר חדש יותר` : ''}
          </span>
        ) : null}
        {item.source === 'manual' && item.manual ? (
          <span className="sub">
            עודכן {fmtDate(item.manual.updatedAt)}
            {item.last ? ` · אחרון במסמך ${fmtMoney(item.last.price)}` : ''}
          </span>
        ) : null}
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {editing ? (
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>ביטול</Button>
        ) : (
          <>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setValue(item.price == null ? '' : String(item.price));
                setEditing(true);
              }}
            >
              קביעת מחיר
            </Button>
            {item.source === 'manual' ? <Button size="sm" variant="ghost" onClick={() => save(null)} loading={busy} title="חזרה למחיר האחרון מהמסמכים או למחיר הבסיס">הסרה</Button> : null}
          </>
        )}
      </td>
    </tr>
  );
}
